import React from 'react';
import { Box, Popper, Paper, Button } from '@mui/material';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';

interface GridCellExpandProps {
  value: any;
  width: number;
  onClickLoadMore?: React.MouseEventHandler<HTMLButtonElement>;
}

function isOverflown(element: Element): boolean {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

const GridCellExpand = React.memo(function GridCellExpand(
  props: GridCellExpandProps
) {
  const { width, value, onClickLoadMore } = props;
  const wrapper = React.useRef<HTMLDivElement | null>(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current!);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent: KeyboardEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: 'center',
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: '100%',
          width,
          display: 'block',
          position: 'absolute',
          top: 0,
        }}
      />
      <Box
        ref={cellValue}
        sx={{
          overflow: 'hidden',
          display: 'flex',
          gap: 2,
        }}
      >
        {value}
      </Box>
      <Popper
        open={showFullCell && anchorEl !== null}
        anchorEl={anchorEl}
        style={{ width }}
      >
        <Paper elevation={4}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: -6, p: 2 }}>
            {value}
          </Box>
          {Boolean(onClickLoadMore) && (
            <Box sx={{ p: 2, mt: -2 }}>
              <Button
                onClick={onClickLoadMore}
                endIcon={<KeyboardDoubleArrowDownIcon />}
              >
                Load More
              </Button>
            </Box>
          )}
        </Paper>
      </Popper>
    </Box>
  );
});

export default GridCellExpand;
